<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Eventos | Lugares" />

    <div v-if="event" class="d-flex flex-wrap align-center ga-2 mb-6">
      <div class="mr-4">
        <div class="text-h6 font-weight-bold">
          {{ event.title }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ eventDate }}<template v-if="mappingName">
            · {{ mappingName }}
          </template>
        </div>
      </div>
      <template v-if="board.sectors.length">
        <v-chip
          v-for="status in statuses"
          :key="status"
          size="small"
          variant="outlined"
        >
          <seat-status-swatch :status="status" class="mr-2" />
          {{ board.totals[status] }} {{ SeatStatusPlurals[status] }}
        </v-chip>
      </template>
    </div>

    <v-alert
      v-if="loaded && !board.sectors.length"
      type="info"
      variant="tonal"
      icon="fa-solid fa-chair"
      text="Este evento não tem lugares marcados."
    />

    <div v-else-if="section" class="seats d-flex ga-4">
      <nav class="sections">
        <template v-for="sector in board.sectors" :key="sector.id">
          <div class="text-overline sections__sector">
            {{ sector.name }}
          </div>
          <button
            v-for="item in sector.sections"
            :key="item.id"
            type="button"
            class="sections__item"
            :class="{ 'sections__item--current': item.id === sectionId }"
            @click="openSection(item.id)"
          >
            <span class="sections__name">{{ item.name }}</span>
            <span
              v-if="board.counts(item.id).blocked"
              v-tooltip="plural(board.counts(item.id).blocked, 'bloqueado', 'bloqueados')"
              class="sections__blocked"
              :style="{ background: SeatStatusColors[SeatStatus.BLOCKED].background, color: SeatStatusColors[SeatStatus.BLOCKED].color }"
            >
              <v-icon icon="fa-solid fa-lock" size="9" /> {{ board.counts(item.id).blocked }}
            </span>
            <span class="sections__free">{{ board.counts(item.id).available }} livres</span>
          </button>
        </template>
      </nav>

      <div class="main flex-grow-1">
        <div class="d-flex align-center flex-wrap ga-2 mb-2">
          <strong>{{ sectorName }} › {{ section.name }}</strong>
          <v-spacer />
          <div
            v-tooltip="polling.paused ? 'Pausada enquanto a aba está escondida' : `Os status se atualizam sozinhos a cada ${polling.seconds} s`"
            class="freshness"
            :class="{ 'text-warning': polling.failed }"
          >
            <v-progress-circular
              :model-value="polling.paused ? 0 : (polling.elapsed / polling.seconds) * 100"
              :indeterminate="polling.running"
              size="16"
              width="2"
              color="primary"
            />
            <span>{{ freshness }}</span>
            <v-btn
              size="x-small"
              variant="text"
              class="text-none"
              prepend-icon="fa-solid fa-rotate"
              :disabled="polling.running"
              @click="polling.refreshNow()"
            >
              Atualizar agora
            </v-btn>
          </div>
        </div>

        <v-alert
          v-if="notice"
          :type="notice.kind"
          variant="tonal"
          density="compact"
          closable
          class="mb-2"
          :text="notice.text"
          @click:close="notice = null"
        />

        <seat-blocking-grid
          :section="section"
          :selection="selection"
          :flashing="flashing"
          @notice="notice = $event"
        />
      </div>

      <seat-blocking-panel
        :board="board"
        :section="section"
        :sector-name="sectorName"
        :selection="selection"
        :applying="applying"
        @block="apply('block')"
        @unblock="apply('unblock')"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
// The admin's seat blocking page (IDV-705): one Section's grid at a time, a selection, and a panel whose buttons block
// or unblock it right away. The statuses refresh every 10 s, and changes made elsewhere are flashed and named.
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, type Ref, type ShallowRef } from 'vue'
import { useRoute } from 'vue-router'
import { isAxiosError } from 'axios'
import { DateTime } from 'luxon'
import TPageTitle from '@/components/TPageTitle.vue'
import EventService from '@/services/EventService'
import { useLoader } from '@/stores/loader'
import { SeatStatus, SeatStatusColors, SeatStatusPlurals } from '@/constants/SeatStatuses'
import type Event from '@/types/Event'
import type { SeatConflict } from '@/types/EventSeats'
import {
  conflictNotice,
  joinNames,
  plural,
  pollNotice,
  useSeatBoard,
  type BoardSection,
  type Notice,
  type StatusChange,
} from '@/helpers/seatBlocking/board'
import { useSeatSelection, type SeatAction } from '@/helpers/seatBlocking/selection'
import { usePolling } from '@/helpers/seatBlocking/polling'
import SeatBlockingGrid from './seat-blocking-grid.vue'
import SeatBlockingPanel from './seat-blocking-panel.vue'
import SeatStatusSwatch from './seat-status-swatch.vue'

defineOptions({ name: 'EventSeats' })

const FLASH_MS = 3200

const route = useRoute()
const loader = useLoader()
const service = new EventService()
const eventId = route.params.event as string

/** What each panel button sends, the status its seats end in, and how the outcome is told */
const ACTIONS: Record<SeatAction, { send: (ids: string[]) => Promise<unknown>, to: SeatStatus, one: string, many: string, verb: string }> = {
  block: {
    send: ids => service.blockSeats(eventId, ids),
    to: SeatStatus.BLOCKED,
    one: 'lugar bloqueado',
    many: 'lugares bloqueados',
    verb: 'bloquear',
  },
  unblock: {
    send: ids => service.unblockSeats(eventId, ids),
    to: SeatStatus.AVAILABLE,
    one: 'lugar desbloqueado',
    many: 'lugares desbloqueados',
    verb: 'desbloquear',
  },
}

const statuses: SeatStatus[] = Object.values(SeatStatus)

const event: ShallowRef<Event | null> = shallowRef(null)
const loaded: Ref<boolean> = ref(false)
const sectionId: Ref<string | null> = ref(null)
const notice: Ref<Notice | null> = ref(null)
const applying: Ref<SeatAction | null> = ref(null)
const flashing: Ref<Set<string>> = ref(new Set())
let flashTimer: ReturnType<typeof setTimeout> | undefined

const board = useSeatBoard()
const selection = useSeatSelection(board, () => sectionId.value)
const polling = usePolling({ refresh: poll, busy: () => selection.dragging || applying.value !== null })

const section = computed<BoardSection | undefined>(() => (sectionId.value ? board.section(sectionId.value) : undefined))

const sectorName = computed<string>(() => board.sectors.find(sector => sector.id === section.value?.sectorId)?.name ?? '')

const eventDate = computed<string>(() => (event.value ? DateTime.fromSQL(event.value.dates.start).toFormat('dd/MM/yyyy HH:mm') : ''))

const mappingName = computed<string>(() => event.value?.configuration?.mapping?.name ?? '')

const freshness = computed<string>(() => {
  if (polling.paused) return 'Atualização pausada'
  if (polling.failed) return `Falha ao atualizar há ${polling.elapsed} s`
  return `Atualizado há ${polling.elapsed} s`
})

function openSection(id: string) {
  sectionId.value = id
  selection.clear()
}

function flash(changes: StatusChange[]) {
  if (!changes.length) return

  clearTimeout(flashTimer)
  flashing.value = new Set(changes.map(change => change.seat.id))
  flashTimer = setTimeout(() => {
    flashing.value = new Set()
  }, FLASH_MS)
}

/** Fetches the statuses and flashes what changed elsewhere */
async function reload(): Promise<StatusChange[]> {
  const response = await service.seats(eventId)
  const changes = board.load(response.data.data)

  if (!sectionId.value || !board.section(sectionId.value)) {
    sectionId.value = board.sectors[0]?.sections[0]?.id ?? null
    selection.clear()
  }
  flash(changes)

  return changes
}

/** A refresh: what changed elsewhere is named, and the selected seats that can no longer change leave the selection */
async function poll(): Promise<void> {
  const changes = await reload()
  const found = pollNotice(changes, sectionId.value ?? '', selection.prune())
  if (found) notice.value = found
}

/** The notice for a change of this page, followed by what a refetch found changed elsewhere */
function withElsewhere(text: string, kind: Notice['kind'], changes: StatusChange[]): Notice {
  const elsewhere = pollNotice(changes, sectionId.value ?? '', selection.prune())
  if (!elsewhere) return { text, kind }

  return { text: `${text} ${elsewhere.text}`, kind: kind === 'success' && elsewhere.kind === 'warning' ? 'warning' : kind }
}

async function apply(action: SeatAction) {
  if (applying.value || !selection.seatsFor(action).length) return

  applying.value = action
  try {
    // A poll already in flight lands first, so its older statuses can't overwrite this change
    await polling.settle()
    const ids = [ ...selection.seatsFor(action) ]
    if (!ids.length) return

    const { send, to, one, many, verb } = ACTIONS[action]
    const names = joinNames(ids.map(board.label))
    try {
      await send(ids)
    } catch (error) {
      if (!isAxiosError<SeatConflict>(error) || error.response?.status !== 409) {
        notice.value = { text: `Não foi possível ${verb} ${names}.`, kind: 'error' }
        return
      }

      // All or nothing: nothing changed. Show the new statuses and leave the seats that moved out of the selection
      const conflicts = error.response.data.seats.map(seat => seat.seat_id)
      const changes = await reload().catch((): StatusChange[] => [])
      selection.remove(conflicts)
      const named = conflictNotice(conflicts.map(board.label))
      notice.value = withElsewhere(named.text, named.kind, changes.filter(change => !conflicts.includes(change.seat.id)))
      return
    }

    board.setStatuses(ids, to)
    const changes = await reload().catch((): StatusChange[] => [])
    notice.value = withElsewhere(`${plural(ids.length, one, many)}: ${names}.`, 'success', changes)
  } finally {
    applying.value = null
    polling.restart()
  }
}

function onKey(event: KeyboardEvent) {
  const target = event.target as HTMLElement | null
  if (target?.closest('input, textarea, [contenteditable]')) return
  if (event.ctrlKey || event.metaKey || event.altKey) return

  const key = event.key.toLowerCase()
  if (key === 'escape') selection.clear()
  else if (key === 'b') void apply('block')
  else if (key === 'd') void apply('unblock')
}

onMounted(async () => {
  window.addEventListener('keydown', onKey)

  try {
    loader.increment()
    const [ shown ] = await Promise.all([service.show(eventId), reload()])
    event.value = shown.data.data
    loaded.value = true
    polling.start()
  } finally {
    loader.decrement()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  polling.stop()
  clearTimeout(flashTimer)
})
</script>

<style scoped>
.seats {
  align-items: flex-start;
}

.sections {
  width: 190px;
  flex: none;
  padding-right: 12px;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

.sections__sector {
  margin-top: 8px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.55);
}

.sections__item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 14px;
  text-align: left;
}

.sections__item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.sections__item--current {
  background: rgba(var(--v-theme-primary), 0.12) !important;
  font-weight: 600;
}

.sections__name {
  flex: 1;
}

.sections__blocked {
  padding: 0 5px;
  border-radius: 8px;
  font-size: 11px;
}

.sections__free {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
}

.main {
  min-width: 0;
}

.freshness {
  display: flex;
  flex: none;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
